import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

type AdminDb = {
  id: string;
  user_id: string;
  admin_email: string;
  db_name: string;
  project_name: string | null;
  db_password: string | null;
  created_at: string;
  updated_at: string;
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [entries, setEntries] = useState<AdminDb[]>([]);
  const [loading, setLoading] = useState(true);

  const [adminEmail, setAdminEmail] = useState('');
  const [form, setForm] = useState({
    db_name: '',
    project_name: '',
    db_password: '',
  });

  const [filterEmail, setFilterEmail] = useState('');
  const [filterDbName, setFilterDbName] = useState('');

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchEntries = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('admin_databases')
      .select('*')
      .eq('user_id', user!.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar registros:', error);
      alert('Erro ao carregar registros. Tente novamente.');
    } else {
      setEntries(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!adminEmail.trim() || !form.db_name.trim()) {
      alert('Informe o e-mail e o nome do banco.');
      return;
    }

    const { data, error } = await supabase
      .from('admin_databases')
      .insert({
        user_id: user.id,
        admin_email: adminEmail.trim(),
        db_name: form.db_name.trim(),
        project_name: form.project_name.trim() || null,
        db_password: form.db_password || null
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao cadastrar banco:', error);
      alert('Erro ao cadastrar banco. Tente novamente.');
      return;
    }

    setEntries(prev => [data as AdminDb, ...prev]);
    setForm({ db_name: '', project_name: '', db_password: '' });
    alert('Banco cadastrado com sucesso!');
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deseja excluir este registro?')) return;
    const { error } = await supabase
      .from('admin_databases')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao excluir banco:', error);
      alert('Erro ao excluir. Tente novamente.');
      return;
    }
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const filtered = entries.filter(e => {
    const emailMatch = filterEmail.trim() ? e.admin_email.toLowerCase().includes(filterEmail.trim().toLowerCase()) : true;
    const dbNameMatch = filterDbName.trim() ? e.db_name.toLowerCase().includes(filterDbName.trim().toLowerCase()) : true;
    return emailMatch && dbNameMatch;
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEOHead
          title="Admin - Controle de Bancos - Vibecode"
          description="Acesso restrito. Faça login para gerenciar bancos."
          canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/admin`}
        />
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Acesso Negado</h2>
          <p className="text-gray-600 mb-6">Você precisa estar logado para acessar o admin.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer"
          >
            Voltar ao Início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Admin - Controle de Bancos - Vibecode"
        description="Página administrativa para cadastrar e listar bancos por e-mail usado no Supabase."
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/admin`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Admin - Controle de Bancos",
          "description": "Administre bancos de dados associados ao seu e-mail do Supabase.",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/admin`
        }}
      />

      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-indigo-600" style={{ fontFamily: "Pacifico, serif" }}>
                Vibecode
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a onClick={() => navigate('/dashboard')} className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">Dashboard</a>
              <span className="text-indigo-600 font-medium cursor-pointer">Admin</span>
              <a onClick={() => navigate('/')} className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">Início</a>
            </nav>
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Controle de Banco de Dados (Admin)</h1>
          <p className="text-gray-600">Cadastre o e-mail usado no Supabase e associe bancos de dados a esse e-mail.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">E-mail do Supabase</h3>
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="ex: voce@empresa.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-2">
              Use este e-mail para cadastrar bancos abaixo.
            </p>
          </div>

          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cadastrar Banco para este e-mail</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Nome do Banco *</label>
                  <input
                    type="text"
                    value={form.db_name}
                    onChange={(e) => setForm({ ...form, db_name: e.target.value })}
                    placeholder="ex: my_database"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Projeto</label>
                  <input
                    type="text"
                    value={form.project_name}
                    onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                    placeholder="ex: projeto-x"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Senha</label>
                  <input
                    type="text"
                    value={form.db_password}
                    onChange={(e) => setForm({ ...form, db_password: e.target.value })}
                    placeholder="ex: senha123"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Cadastrar Banco
                </button>
              </div>
            </form>
            <p className="text-xs text-gray-500 mt-3">
              Atenção: armazenar senhas em texto puro não é recomendado em produção.
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Bancos cadastrados</h3>
            <button
              onClick={fetchEntries}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer"
            >
              Atualizar
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <input
              type="text"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder="Filtrar por e-mail..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="text"
              value={filterDbName}
              onChange={(e) => setFilterDbName(e.target.value)}
              placeholder="Filtrar por nome do banco..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando registros...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-database-2-line text-5xl text-gray-300 mb-2"></i>
              <p className="text-gray-600">Nenhum banco encontrado para os filtros aplicados.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-mail</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome do Banco</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projeto</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Senha</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map((e) => (
                    <tr key={e.id}>
                      <td className="px-4 py-2 text-sm text-gray-900">{e.admin_email}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{e.db_name}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{e.project_name || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{e.db_password || '-'}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleDelete(e.id)}
                          className="text-red-600 hover:text-red-700 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                          <span className="sr-only">Excluir</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}